import React from 'react';
import { 
  Grid, 
  Card, 
  Box, 
  Typography, 
  alpha, 
  useTheme, 
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as CpuIcon,
  Search as SearchIcon,
  Description as DocumentIcon,
  Public as ClusterIcon,
  ViewModule as ShardIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

/**
 * Component to display OpenSearch cluster statistics summary
 * 
 * @param {Object} props
 * @param {Object} props.data - OpenSearch statistics data
 * @param {boolean} props.loading - Loading state
 * @param {function} props.onRefresh - Function to refresh data
 */
const OpenSearchStats = ({ data, loading = false, onRefresh }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={item}>
            <Card
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                backdropFilter: 'blur(8px)',
                background: alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Box sx={{ width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InfoIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.2) }} />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }
  
  if (!data) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.6, mb: 1 }} />
        <Typography variant="h6" gutterBottom>No OpenSearch Data</Typography>
        <Typography variant="body2" color="text.secondary">
          No OpenSearch statistics data available
        </Typography>
        {onRefresh && (
          <IconButton 
            onClick={onRefresh} 
            sx={{ mt: 2 }}
            aria-label="Refresh OpenSearch stats"
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>
    );
  }
  
  // Helper function to format large numbers
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num === 0) return '0';
    
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  const formatBytes = (bytes, decimals = 1) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };
  
  // Prepare stats data
  const stats = {
    clusters: data.clusters_count || 1,
    nodes: data.total_nodes || data.number_of_nodes || 0,
    dataNodes: data.total_data_nodes || data.number_of_data_nodes || 0,
    indices: data.total_indices || 0,
    shards: data.total_shards || data.active_shards || 0,
    primaryShards: data.total_primary_shards || data.active_primary_shards || 0,
    documents: data.total_documents || 0,
    storageSize: data.total_store_size_bytes || 0,
    queries: data.total_search_queries || 0,
    indexing: data.total_indexing_operations || 0,
    avgCpu: data.avg_cpu_percent || 0,
    avgMemory: data.avg_memory_percent || 0,
    avgDisk: data.avg_disk_percent || 0,
    avgJvmHeap: data.avg_heap_percent || 0
  };
  
  // Define stat cards
  const statCards = [
    {
      title: 'Clusters',
      value: stats.clusters,
      icon: <ClusterIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />,
      tooltip: 'Total number of OpenSearch clusters'
    },
    {
      title: 'Nodes',
      value: `${stats.nodes} (${stats.dataNodes} data)`,
      icon: <StorageIcon sx={{ fontSize: 36, color: theme.palette.info.main }} />,
      tooltip: 'Total number of OpenSearch nodes'
    },
    {
      title: 'Indices',
      value: formatNumber(stats.indices),
      icon: <SearchIcon sx={{ fontSize: 36, color: theme.palette.success.main }} />,
      tooltip: 'Total number of indices across all clusters'
    },
    {
      title: 'Shards',
      value: `${formatNumber(stats.shards)} (${formatNumber(stats.primaryShards)} primary)`,
      icon: <ShardIcon sx={{ fontSize: 36, color: theme.palette.warning.main }} />,
      tooltip: 'Total number of shards across all clusters'
    },
    {
      title: 'Documents',
      value: formatNumber(stats.documents),
      icon: <DocumentIcon sx={{ fontSize: 36, color: theme.palette.secondary.main }} />,
      tooltip: 'Total number of documents indexed'
    },
    {
      title: 'Storage',
      value: formatBytes(stats.storageSize),
      icon: <StorageIcon sx={{ fontSize: 36, color: theme.palette.error.main }} />,
      tooltip: 'Total storage size used by indices'
    }
  ];
  
  // Resource usage stats
  const resourceStats = [
    {
      title: 'CPU Usage',
      value: `${stats.avgCpu.toFixed(1)}%`,
      icon: <CpuIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />,
      tooltip: 'Average CPU usage across all nodes'
    },
    {
      title: 'Memory Usage',
      value: `${stats.avgMemory.toFixed(1)}%`,
      icon: <MemoryIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />,
      tooltip: 'Average memory usage across all nodes'
    },
    {
      title: 'Disk Usage',
      value: `${stats.avgDisk.toFixed(1)}%`,
      icon: <StorageIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />,
      tooltip: 'Average disk usage across all nodes'
    },
    {
      title: 'JVM Heap',
      value: `${stats.avgJvmHeap.toFixed(1)}%`,
      icon: <MemoryIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />,
      tooltip: 'Average JVM heap usage across all nodes'
    }
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="medium">OpenSearch Cluster Overview</Typography>
        {onRefresh && (
          <IconButton 
            size="small" 
            onClick={onRefresh}
            aria-label="Refresh OpenSearch stats"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      
      <Grid container spacing={3}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Tooltip title={stat.tooltip} arrow>
              <Card
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                  backdropFilter: 'blur(8px)',
                  background: alpha(theme.palette.background.paper, 0.8),
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {stat.icon}
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </Box>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
      
      <Divider sx={{ my: 4, opacity: 0.6 }} />
      
      <Typography variant="h6" fontWeight="medium" mb={3}>Resource Utilization</Typography>
      
      <Grid container spacing={3}>
        {resourceStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Tooltip title={stat.tooltip} arrow>
              <Card
                sx={{
                  p: 2.5,
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                  backdropFilter: 'blur(8px)',
                  background: alpha(theme.palette.background.paper, 0.8),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.4), mr: 2 }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {stat.value}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default OpenSearchStats; 